<template>
  <EditorPanel
    v-if="profile"
    class="pb-channels-panel"
    :loading="loading"
    @deselect="deselectChannel"
  >
    <template #header>
      <RouterTabList
        class="pb-modes-list"
        :add-title="translate('pb2.channelsListView.addModeTooltip')"
        @add="addMode"
      >
        <RouterTabItem
          :to="{
            name: routeNames.GlobalEditor,
            params: {
              profileUid: profileStore.profileUid,
            },
          }"
          :title="translate('pb2.channelsListView.templateChannelsTooltip')"
        >
          <span class="pb-mode-tab-name ellipsis">
            {{ translate('pb2.channelsListView.templateChannelsLabel') }}
          </span>
        </RouterTabItem>
        <template v-if="!modesOverflow">
          <RouterTabItem
            v-for="iteratedMode in modes"
            :key="iteratedMode.data._uid"
            :to="iteratedMode.link"
            :title="iteratedMode.linkName"
          >
            <span class="pb-mode-tab-name ellipsis">
              {{ iteratedMode.shortName }}<br>
              <PbItemDetails class="pb-mode-tab-details">
                {{ getModeDetails(iteratedMode) }}
              </PbItemDetails>
            </span>
            <ReportIcon
              class="pb-mode-tab-report"
              :report="iteratedMode.report"
            />
          </RouterTabItem>
        </template>
        <RouterTabItem
          v-else-if="visibleMode"
          :to="visibleMode.link"
          :title="visibleMode.linkName"
        >
          <span class="pb-mode-tab-name ellipsis">
            {{ visibleMode.shortName }}<br>
            <PbItemDetails class="pb-mode-tab-details">
              {{ getModeDetails(visibleMode) }}
            </PbItemDetails>
          </span>
          <ReportIcon
            class="pb-mode-tab-report"
            :report="visibleMode.report"
          />
        </RouterTabItem>
        <BaseTab nopadding>
          <BaseSelect
            v-model="modeStore.visibleModeUid"
            size="small"
            class="pb-modes-overflow"
            :search="showModeSearch"
            :options="modeOptions"
            :focus="modeStore.visibleModeUid"
            @input="navigateToMode"
          >
            <template #toggler="togglerProps">
              <SelectDropdownToggler
                v-bind="togglerProps"
                :label="dropdownLabel"
                class="pb-router-tab-button pb-modes-overflow-toggler"
                :class="{
                  'pb-modes-overflow-toggler-with-label': !!dropdownLabel,
                  'pb-modes-overflow-toggler-without-label': !dropdownLabel,
                }"
              />
            </template>

            <template #default="option">
              <span class="pb-mode-tab-dropdown-name">
                {{ option.label }}<br>
                <PbItemDetails class="pb-mode-tab-details">
                  {{ getModeDetails(option) }}
                </PbItemDetails>
              </span>
              <ReportIcon
                class="pb-mode-tab-report"
                :report="option.report"
              />
            </template>

            <template #static="{ close }">
              <ControlButton
                class="pb-reorder-modes-button mg-h-medium mg-b-medium"
                :class="{
                  'mg-t-medium': !showModeSearch,
                }"
                :label="translate('pb2.channelsListView.manageModesLabel')"
                :disabled="readonly"
                :to="manageModesLink"
                :onclick="close"
              />
            </template>
          </BaseSelect>
        </BaseTab>
      </RouterTabList>
    </template>

    <ul v-if="channels && channels.length">
      <template
        v-for="channel in channels"
        :key="channel.data._uid"
      >
        <PbLink
          v-slot="{
            navigate, href, isActive,
          }"
          custom
          :to="channel.link"
        >
          <!-- Other available props from v-slot: href, route -->
          <ChannelItem
            :channel="channel"
            :href="href"
            :active="isActive"
            :drag-drop-context="dragDropContext"
            @navigate="navigate"
          />
        </PbLink>
      </template>
    </ul>
    <PanelMessage v-else>
      <template v-if="isGlobal">
        {{ translate('pb2.channelsListView.noTemplateChannelsMessage') }}
      </template>
      <template v-else>
        {{ translate('pb2.channelsListView.noChannelsMessage') }}
      </template>
      <ControlButton
        class="btn-success"
        :label="translate('pb2.channelsListView.addChannelsLabel')"
        :disabled="readonly"
        :to="addChannelLink"
      />
    </PanelMessage>

    <template #footer>
      <ButtonGroup
        row
        padded
      >
        <ControlButton
          :to="addChannelLink"
          :disabled="readonly"
        >
          <span class="ellipsis">{{ translate('pb2.channelsListView.addChannelsLabel') }}</span>
        </ControlButton>
      </ButtonGroup>
    </template>
  </EditorPanel>
</template>

<script setup>
import {
  computed,
  ref,
  watch,
} from 'vue'
import dev from '@nicolaudie-group/ng-ui-kit/helpers/dev'
import ControlButton from '@nicolaudie-group/ng-ui-kit/components/ControlButton/ControlButton.vue'
import BaseTab from '@nicolaudie-group/ng-ui-kit/components/BaseTab/BaseTab.vue'
import useMediaFallback from '@nicolaudie-group/ng-ui-kit/composables/useMediaFallback'
import BaseSelect from '@nicolaudie-group/ng-ui-kit/components/Select/BaseSelect.vue'
import SelectDropdownToggler from '@nicolaudie-group/ng-ui-kit/components/Select/SelectDropdownToggler.vue'

import useIsGlobal from '@/composables/useIsGlobal'
import { useDragDropContext } from '@/composables/useDragDrop'
import useDestination from '@/composables/useDestination'
import { useCurrent, useModeStatus, useReadonly } from '@/composables/useProfile'
import useTranslate from '@/composables/useTranslate'

import routeNames from '@/router/names'

import useModeStore from '@/stores/mode.store'
import useRouterStore from '@/stores/router.store'

import ChannelItem from '@/components/ChannelItem/ChannelItem.vue'
import EditorPanel from '@/components/EditorPanel/EditorPanel.vue'
import RouterTabList from '@/components/RouterTabs/RouterTabList.vue'
import RouterTabItem from '@/components/RouterTabs/RouterTabItem.vue'
import ReportIcon from '@/components/ItemIcons/ReportIcon.vue'
import ButtonGroup from '@/components/ButtonGroup/ButtonGroup.vue'
import PbLink from '@/components/PbLink.vue'
import PanelMessage from '@/components/PanelMessage/PanelMessage.vue'
import { getModeDetails } from '@/composables/useModeDetails'
import PbItemDetails from '@/components/PbItemDetails/PbItemDetails.vue'
import useProfileStore from '@/stores/profile.store'

const { isGlobal } = useIsGlobal()

const { translate } = useTranslate()

const modeStore = useModeStore()
const profileStore = useProfileStore()
const routerStore = useRouterStore()

const {
  profile,
  channels,
  modes,
} = useCurrent()
const readonly = useReadonly()

const loading = useModeStatus().pending

/*
Goal of the tabs threshold is not to make them readable, but clickable.
Too many of them and they either become to small (if no min width) or
we have to scroll too much.

By using the tabs threshold, we can limit the number of tabs displayed.
*/
const tabsThreshold = useMediaFallback([
  6, // On TV devices, only show visibleMode when there are more than 6 tabs
  4, // On desktop devices, only show visibleMode when there are more than 4 tabs
  2, // On tablet devices, only show visibleMode when there are more than 2 tabs
  1, // On mobile devices, only show visibleMode when there is more than 1 tab
])

const modesOverflow = computed(() => (
  modes.value?.length >= tabsThreshold.value
))

const dropdownLabel = computed(() => {
  if (modesOverflow.value) {
    return `+${modes.value.length - 1}`
  }
  return ''
})

const modeOptions = computed(() => modes.value?.map((modeItem) => ({
  label: modeItem.shortName,
  value: modeItem.data._uid,
  defaultName: modeItem.defaultName,
  footprint: modeItem.footprint,
  report: modeItem.report,
  search: modeItem.linkName,
})) || [])

const visibleMode = computed(() => {
  const uid = modeStore.visibleModeUid
  return modes.value?.find((modeItem) => modeItem.data._uid === uid)
})

/**
 * Flag that's set when we need to navigate after changing the visibleModeUid
 */
const navigateRef = ref(false)
function navigateToMode() {
  navigateRef.value = true
}

// When navigate or the UID of the visibleMode changes
watch([navigateRef, () => visibleMode.value?.data?._uid], ([navigate]) => {
  // Check that navigate is true, indicating the change is due to input on the select
  if (navigate) {
    // Reset the "navigate" flag
    navigateRef.value = false
    // Redirect to the new visible mode
    routerStore.push(visibleMode.value?.link)
  }
})

watch(() => profileStore.modeUid, (newModeUid) => {
  // When the current mode changes
  if (newModeUid) {
    // If we're still looking at modes, update the visibleMode
    modeStore.visibleModeUid = newModeUid
  } else if (!modeStore.visibleModeUid) {
    // Otherwise, if there is no visibleMode yet, choose the first mode as visibleMode
    modeStore.visibleModeUid = modeOptions.value[0]?.value
  }
}, { immediate: true })

const addChannelLink = computed(() => {
  const global = isGlobal.value
  const currentRoute = routerStore.route
  const { profileUid, modeUid, channelUid } = profileStore
  if (readonly.value) {
    return currentRoute
  }
  const params = {
    profileUid,
  }
  if (!global) {
    params.modeUid = modeUid
  }
  return {
    name: global ? routeNames.AddGlobalChannel : routeNames.AddLocalChannel,
    params,
    // Move the required params to query parameters,
    // so that they're accessible in the dialog
    query: {
      channelUid,
    },
  }
})

const manageModesLink = computed(() => {
  const currentRoute = routerStore.route
  const {
    profileUid,
    modeUid,
    channelUid,
    presetRangeUid,
    presetUid,
  } = profileStore
  if (readonly.value) {
    return currentRoute
  }
  return {
    name: routeNames.ManageModes,
    params: {
      profileUid,
    },
    // Move the required params to query parameters,
    // so that they're accessible in the dialog
    query: {
      modeUid,
      channelUid,
      presetRangeUid,
      presetUid,
    },
  }
})

const showModeSearch = computed(() => !!(modes.value?.length > 3))

const moveChannel = (channelUid, destinationOrder) => {
  if (isGlobal.value) {
    return modeStore.moveGlobalChannel({ _uid: channelUid, _order: destinationOrder })
  }
  return modeStore.moveLocalChannel({ _uid: channelUid, _order: destinationOrder })
}

const dragDropContext = useDragDropContext({
  onDrop(draggedChannelData, dropzoneData) {
    const { before, after, channel: dropzoneChannel } = dropzoneData
    if (draggedChannelData.data._uid !== dropzoneChannel.data._uid) {
      // If the channel is not moving to it's own position
      const sourceOrder = draggedChannelData.data._order
      let destinationOrder = dropzoneChannel.data._order
      if (sourceOrder < destinationOrder && before) {
        // If the channel we're moving was before the channel we're moving to
        // and we dropped before the channel we're moving to...
        // Remove 1 to the destination order, to take into account all orders shifting
        // because of the moving channel
        destinationOrder -= 1
      }
      if (sourceOrder > destinationOrder && after) {
        // If the channel we're moving was after the channel we're moving to
        // and we dropped after the channel we're moving to...
        // Add 1 to the order, to take into account all orders shifting
        // because of the moving channel
        destinationOrder += 1
      }
      if (destinationOrder !== sourceOrder) {
        dev.log(`Moving channel ${draggedChannelData.data.name} to order ${destinationOrder}`)
        moveChannel(draggedChannelData.data._uid, destinationOrder)
      }
    }
  },
})

function addMode() {
  return modeStore.create()
}

const deselectChannelDestination = useDestination(computed(() => ({
  name: isGlobal.value ? routeNames.GlobalEditor : routeNames.Mode,
})))

function deselectChannel() {
  return routerStore.push(deselectChannelDestination.value)
}
</script>

<style lang="scss">
@use '@nicolaudie-group/ng-ui-kit/defines/_fonts.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_colors.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_config.define.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_flexbox.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_generic.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_responsive.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_theme.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_animations.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_transitions.mixin.scss';

@use '/src/styles/_pb.scss';

@use '@nicolaudie-group/ng-ui-kit/components/BaseTab/tabs.scss';
@use '@nicolaudie-group/ng-ui-kit/components/Listbox/listbox.scss';

.pb-modes-list {
  /*
  Used to bring dragged channels in front of modes select dropdown.
  But breaks dropdown on safari when there is overflow on parent component.
  https://bugs.webkit.org/show_bug.cgi?id=160953
  */
  // z-index: pb.$max-zindex - 210;
}

.pb-mode-tab-report {
  display: inline-block;
}

.pb-mode-tab-report {
  float: right;
}

@include listbox.item-label-hover {
  .pb-mode-tab-details {
    color: colors.$color-default--white;
  }
}

.pb-modes-overflow {
  height: 100%;

  .dropdown-wrapper {
    height: 100%;
  }

  &-toggler {
    padding: tabs.$padding;
    height: 100%;
    outline-color: transparent;

    &-without-label {
      .ui-select-dropdown-toggler-icon {
        margin: 0;
      }
    }

  }

}

.pb-mode-tab-name {
  max-width: 120px;
}

.pb-mode-tab-dropdown-name {
  flex-grow: 1;
}
</style>
